'use client';

import { useMemo, useState } from 'react';

import { useBreakingCategories, useBreakingEventsList } from '@/services/query';
import type { BreakingNewsCategory } from '@/types/breaking.types';

export const useBreaking = () => {
  // State
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);

  // Fetch categories
  const {
    data: apiCategories = [],
    isLoading: isCategoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useBreakingCategories();

  // Add a frontend "All" category at the beginning of the list
  const allCategory: BreakingNewsCategory = {
    name: 'All',
    slug: 'all',
    description: 'All breaking news',
    tagId: null,
  };
  const categories: BreakingNewsCategory[] = [allCategory, ...apiCategories];

  const {
    data: markets = [],
    isLoading: isEventsLoading,
    error: eventsError,
    refetch: refetchEvents,
  } = useBreakingEventsList({
    limit: 20,
    offset: 0,
    order: 'createdAt',
    ascending: false,
    type: 'breaking-news' as const,
    window: '24h',
    tag_id: selectedTagId,
  });

  // Sort markets by absolute price change descendingly
  const sortedMarkets = useMemo(() => {
    return [...markets].sort((a, b) => {
      const absA = Math.abs(a?.price_change ?? 0);
      const absB = Math.abs(b?.price_change ?? 0);
      return absB - absA;
    });
  }, [markets]);

  // Combined error state
  const error = categoriesError || eventsError || null;

  // Handlers
  const handleCategoryClick = (tagId: string | null) => {
    setSelectedTagId(tagId);
  };

  const refetch = () => {
    refetchCategories();
    refetchEvents();
  };

  return {
    // Data
    categories,
    markets: sortedMarkets,
    selectedTagId,

    // States
    isCategoriesLoading,
    isEventsLoading,
    error,

    // Handlers
    handleCategoryClick,
    refetch,
  };
};
